/**
 * 메뉴 아이콘 한 벌 (v2.317.0 · Salesforce 식 object icon).
 *
 *   - 흰 선 그림(lucide) + **종류별 바탕색** 둥근 네모. 같은 아이콘이 메뉴·검색 결과·최근 본 항목·카드 머리에 반복되어
 *     「이건 영업건, 이건 매입」을 알려 준다. 이모지는 운영체제마다 그림이 달라 뺐다.
 *   - 종류 색(SLDS 표준 아이콘 색): 영업 주황 · 물류(재고·매입·수출입) 초록 · 문서·결재 파랑 · 분석·도구 청록 ·
 *     거래처·관리 남보라 · 일정 보라 · 비상벨 빨강.
 *   - Salesforce 아이콘 그림 자체는 가져오지 않는다(저작권) — 규칙만 따르고 그림은 lucide(ISC).
 */
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard, CalendarDays, ClipboardList, Handshake, Wrench, Siren, Package, ShoppingCart, Ship,
  Inbox, FolderOpen, Archive, Mail, HardDrive, TrendingUp, Tags, Megaphone, Building2, History, Settings, MessageSquarePlus,
  Search, Star, Bell, User, Landmark, Leaf, ListChecks, Users,
} from 'lucide-react';

export type MenuIconName =
  | 'dashboard' | 'calendar' | 'sales' | 'deal' | 'maintenance' | 'bell' | 'stock' | 'purchase' | 'imports'
  | 'approvals' | 'documents' | 'expenses' | 'mail' | 'nas' | 'analysis' | 'price' | 'g2b' | 'sizing' | 'marketing'
  | 'partners' | 'audit' | 'system' | 'devreq' | 'search' | 'star' | 'notice' | 'user' | 'tasks' | 'staff';

const ICON: Record<MenuIconName, LucideIcon> = {
  dashboard: LayoutDashboard, calendar: CalendarDays, sales: ClipboardList, deal: Handshake, maintenance: Wrench,
  bell: Siren, stock: Package, purchase: ShoppingCart, imports: Ship, approvals: Inbox, documents: FolderOpen,
  expenses: Archive, mail: Mail, nas: HardDrive, analysis: TrendingUp, price: Tags, g2b: Landmark, sizing: Leaf,
  marketing: Megaphone, partners: Building2, audit: History, system: Settings, devreq: MessageSquarePlus,
  search: Search, star: Star, notice: Bell, user: User,
  tasks: ListChecks, staff: Users,
};

/** 종류 색 — 한 벌. 같은 종류는 어디서나 같은 색. */
const TONE: Record<MenuIconName, string> = {
  dashboard: '#0176D3', calendar: '#9602C7',
  sales: '#FF5D2D', deal: '#FF5D2D', maintenance: '#FF5D2D', bell: '#EA001E',
  stock: '#3BA755', purchase: '#3BA755', imports: '#3BA755',
  approvals: '#1B96FF', documents: '#1B96FF', expenses: '#1B96FF', mail: '#1B96FF', nas: '#1B96FF',
  analysis: '#06A59A', price: '#06A59A', g2b: '#06A59A', sizing: '#06A59A', marketing: '#06A59A',
  partners: '#5867E8', audit: '#5867E8', system: '#5867E8', devreq: '#5867E8', staff: '#5867E8',
  search: '#747474', star: '#747474', notice: '#747474', user: '#747474',
  tasks: '#0176D3',
};

export function menuIconTone(name: MenuIconName): string {
  return TONE[name];
}

/**
 * 둥근 네모 바탕 + 흰 선 그림. size = 바탕 한 변(px). 글자 14px 옆에는 20, 카드 머리에는 24~32.
 * plain=true 면 바탕 없이 선 그림만(현재 색).
 */
export function MenuIcon({
  name,
  size = 20,
  plain = false,
  className = '',
  title,
}: {
  name: MenuIconName;
  size?: number;
  plain?: boolean;
  className?: string;
  title?: string;
}) {
  const Icon = ICON[name];
  const glyph = Math.round(size * 0.65);
  if (plain) return <Icon size={glyph} strokeWidth={2} className={className} aria-hidden="true" />;
  return (
    <span
      className={`inline-grid place-items-center shrink-0 ${className}`}
      style={{ width: size, height: size, borderRadius: Math.max(4, Math.round(size / 4)), background: TONE[name] }}
      title={title}
      aria-hidden="true"
    >
      <Icon size={glyph} strokeWidth={2} color="#fff" />
    </span>
  );
}
